import { formatBillingDate, formatBillingPrice } from '@saaga/shared/utils/billing.util'
import { CalendarIcon, CreditCardIcon, SettingsIcon, UsersIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

import type { CurrentPlanSummary } from './billing.interface'
import { BillingCardDisplay } from './billing-card-display.atom'
import type { SeatInfo } from './billing-seat.interface'
import { BillingSeatManager } from './billing-seat-manager.molecule'

export interface BillingCurrentPlanCardProps {
  summary: CurrentPlanSummary
  onChangePlan?: () => void
  seatInfo?: SeatInfo | null
  isLoadingSeats?: boolean
  isAddingSeats?: boolean
  isRemovingSeats?: boolean
  onAddSeats?: (quantity: number) => void
  onRemoveSeats?: (quantity: number) => void
  className?: string
}

const getPlanPrice = (summary: CurrentPlanSummary): number =>
  summary.billingInterval === 'monthly' ? summary.plan.monthlyPrice : summary.plan.yearlyPrice

const checkIsCancelling = (summary: CurrentPlanSummary): boolean =>
  summary.cancelAt !== null && summary.cancelAt !== undefined

const checkIsFreePlan = (summary: CurrentPlanSummary): boolean =>
  summary.plan.monthlyPrice === 0 && summary.plan.yearlyPrice === 0

const BillingCurrentPlanCardBase: React.FC<BillingCurrentPlanCardProps> = ({
  summary,
  onChangePlan,
  seatInfo,
  isLoadingSeats,
  isAddingSeats,
  isRemovingSeats,
  onAddSeats,
  onRemoveSeats,
  className,
}): ReactNode => {
  const price = getPlanPrice(summary)
  const isCancelling = checkIsCancelling(summary)
  const isFreePlan = checkIsFreePlan(summary)
  const showSeatManager = !isFreePlan && seatInfo && onAddSeats && onRemoveSeats
  const showSeatLoading = !isFreePlan && isLoadingSeats && !seatInfo

  return (
    <Card className={cn('billing-current-plan-card', className)}>
      <CardHeader>
        <CardTitle>Current Plan</CardTitle>
        <CardDescription>Your active subscription</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <PlanDetails
          summary={summary}
          price={price}
          isCancelling={isCancelling}
          onChangePlan={onChangePlan}
        />
        {showSeatLoading && <SeatManagerSkeleton />}
        {showSeatManager && (
          <BillingSeatManager
            seatInfo={seatInfo}
            isAddingSeats={isAddingSeats}
            isRemovingSeats={isRemovingSeats}
            onAddSeats={onAddSeats}
            onRemoveSeats={onRemoveSeats}
          />
        )}
      </CardContent>
    </Card>
  )
}

interface PlanDetailsProps {
  summary: CurrentPlanSummary
  price: number
  isCancelling: boolean
  onChangePlan?: () => void
}

const PlanDetails: React.FC<PlanDetailsProps> = ({
  summary,
  price,
  isCancelling,
  onChangePlan,
}) => (
  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
    <div>
      <PlanPriceHeader
        planName={summary.plan.name}
        price={price}
        billingInterval={summary.billingInterval}
      />
      <PlanMetadata summary={summary} isCancelling={isCancelling} />
    </div>
    {onChangePlan && (
      <Button variant="outline" className="billing-change-plan-button" onClick={onChangePlan}>
        <SettingsIcon className="mr-2 size-4" />
        Change Plan
      </Button>
    )}
  </div>
)

const PlanPriceHeader: React.FC<{ planName: string; price: number; billingInterval: string }> = ({
  planName,
  price,
  billingInterval,
}) => (
  <div className="flex items-baseline gap-2">
    <p className="text-3xl font-bold">{planName}</p>
    <p className="text-lg text-muted-foreground">
      {formatBillingPrice(price, billingInterval as 'monthly' | 'yearly')}
    </p>
  </div>
)

const PlanMetadata: React.FC<{ summary: CurrentPlanSummary; isCancelling: boolean }> = ({
  summary,
  isCancelling,
}) => {
  const dateLabel = isCancelling ? 'Cancels' : 'Renews'
  const displayDate = isCancelling && summary.cancelAt ? summary.cancelAt : summary.renewalDate

  return (
    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
      <div className="flex items-center gap-2">
        <CalendarIcon className="size-4" />
        <span>
          {dateLabel} {formatBillingDate(displayDate)}
        </span>
      </div>
      <PaymentMethodDisplay card={summary.defaultCard} />
    </div>
  )
}

const PaymentMethodDisplay: React.FC<{ card: CurrentPlanSummary['defaultCard'] }> = ({ card }) =>
  card ? (
    <BillingCardDisplay brand={card.brand} last4={card.last4} />
  ) : (
    <div className="flex items-center gap-2">
      <CreditCardIcon className="size-4" />
      <span>No payment method</span>
    </div>
  )

const SeatManagerSkeleton: React.FC = (): ReactNode => (
  <div className="billing-seat-manager-skeleton space-y-3">
    <Separator />
    <div className="flex items-center gap-2">
      <UsersIcon className="size-4 text-muted-foreground" />
      <span className="text-sm font-medium">Seats</span>
    </div>
    <div className="flex items-center justify-between">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-20" />
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-8 w-28" />
      <Skeleton className="h-8 w-32" />
    </div>
  </div>
)

export const BillingCurrentPlanCard = memo(BillingCurrentPlanCardBase)

BillingCurrentPlanCard.displayName = 'BillingCurrentPlanCard'
